import React, {useEffect, useState} from 'react';
import {
  Button,
  IconButton,
  Link,
  ListItemText,
  makeStyles,
  Tooltip
} from '@material-ui/core';
import './TicketingSystemTicketDetails.scss';
import {CloseSharp} from '@material-ui/icons';
import {useTranslation} from 'react-i18next';
import {
  Controller,
  FieldError,
  SubmitErrorHandler,
  SubmitHandler,
  UseControllerOptions,
  useForm
} from 'react-hook-form';
import {getAllPatients} from '@shared/services/PatientsServices';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import HelperUtils from '@shared/utils/HelperUtils';
import {getClinicUsersList} from '@shared/services/ClinicServices';
import {useDispatch, useSelector} from 'react-redux';
import {UserModelMapper} from '@shared/services/service-mappers/ModelMapper';
import {FormField} from '@components/system/FormField/FormField';
import {
  IClaimsTicketDetails,
  IPriorAuthTicketDetails,
  ITicket,
  ITicketDetails,
  IWorkFlowStatus
} from '@shared/services/service-mappers/WorkflowMapper';
import {
  createTicket,
  getTicketDetails,
  updateTicket
} from '@shared/services/WorkflowServices';
import Loader from '@components/framework/loader/Loader';
import {_patientDrawerDetails} from '@shared/store/actions/PatientDrawerAction';
import {
  AppPermissions,
  PriorityOptionsList,
  PatientsTabKeys,
  TICKETING_SYSTEM_COMMON_PERMISSIONS,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import TicketingSystemTicketActivity from './TicketingSystemTicketActivity/TicketingSystemTicketActivity';
import DropDownFieldValue from '@components/system/DropDownFieldValue/DropDownFieldValue';
import {Permission} from '@shared/services/PermissionService';
import {getLastMessage} from '@shared/services/MessageService';
import MessagesConversationDialog from '../../../Sms/components/MessagesConversationDialog/MessagesConversationDialog';
import InfiniteScroll from 'react-infinite-scroll-component';
import {PAGINATION_SIZE} from 'src/constants/SystemConstants';
import {APP_PRIMARY_COLOR} from 'src/style/variable';
import {CommunicationService} from 'src/service/CommunicationService';
import TicketingSystemTimer from '../../TicketingSystemTimer/TicketingSystemTimer';
import useTicketingSystemPermission, {
  IAllPossibleTicketModules,
  IGroupPermission
} from '../../hooks/useTicketingSystemPermission';
import TicketingSystemTicketDetailsModuleFields, {
  ITicketingSystemTicketDetailsModuleFieldsProps
} from './TicketingSystemTicketDetailsModuleFields/TicketingSystemTicketDetailsModuleFields';
import TicketingSystemAttachments from '../../TicketingSystemAttachments/TicketingSystemAttachments';
import {ITicketingSystemTicketActivityItemProps} from './TicketingSystemTicketActivity/TicketActivityItem/TicketActivityItem';
import {
  getFilteredNextSteps,
  getWorkflowStatusLabel,
  isWorkflowEndStatus
} from '../../helpers/utils';

/* eslint-disable camelcase */
export interface IPatient {
  first_name: string;
  middle_name: string;
  last_name: string;
  id: number;
  emr_id: string;
}
/* eslint-enable camelcase */

export interface ITicketInputFields {
  title: string;
  notes: string | null;
  workflowStatusId: IWorkFlowStatus['id'];
  assignedTo: UserModelMapper['id'] | null;
  patient: IPatient;
  priority: number | null;
}

export interface ITicketInputFieldItems {
  [index: string]: any;
  formKey: keyof ITicketInputFields;
  controllerProps?: Pick<UseControllerOptions<ITicketInputFieldItems>, 'rules'>;
}

interface ITicketingSystemTicketDetailsProps {
  clinicGroupId: number;
  clinicId: number;
  ticketId?: number | null;
  workflowId: string;
  onCloseTicketDetails: () => void;
  onSubmit: () => void;
  isPatientNameLink?: boolean;
  hidePatientName?: boolean;
  statusesList: IWorkFlowStatus[];
  refreshTicketListGrid: () => void;
  showTicketDetails: (ticketId: ITicket['id']) => void;
  module: IAllPossibleTicketModules;
  groupPermission?: IGroupPermission;
  renderModuleInputFields?: ITicketingSystemTicketDetailsModuleFieldsProps['renderFields'];
  renderTicketActivityItemContent?: ITicketingSystemTicketActivityItemProps['renderTicketActivityItemContent'];
  messageEventType?: string;
  clinicTicketConfig?: any;
}

const useStyles = makeStyles(() => ({
  listbox: {
    '& li': {
      borderBottom: '1px solid #eeeeee'
    },
    '& li:last-child': {
      borderBottom: 'none'
    }
  },
  listItem: {
    '& .MuiListItemText-secondary': {
      fontSize: '0.9rem',
      marginTop: '5px',
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    },
    '& .MuiListItemText-primary': {
      fontSize: '1.2rem',
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  },
  listItemInChangeValueLink: {
    margin: 0,
    '& .MuiListItemText-secondary': {
      fontSize: '0.9rem',
      marginTop: '5px',
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    },
    '& .MuiListItemText-primary': {
      fontSize: '1.2rem',
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      color: APP_PRIMARY_COLOR
    }
  },
  menuItem: {
    fontSize: '1.2rem',
    paddingBlock: '0.5rem'
  }
}));

export interface IActivitySession {
  pageNumber: number;
  updateComponentKey: number;
}

const TicketingSystemTicketDetails = (
  props: ITicketingSystemTicketDetailsProps
): JSX.Element => {
  const {t} = useTranslation();
  const {
    clinicGroupId,
    clinicId,
    ticketId,
    workflowId,
    onCloseTicketDetails,
    onSubmit,
    isPatientNameLink = true,
    hidePatientName = false,
    statusesList,
    refreshTicketListGrid,
    showTicketDetails,
    module,
    groupPermission,
    renderModuleInputFields,
    renderTicketActivityItemContent,
    messageEventType,
    clinicTicketConfig
  } = props;
  const isCreatingTicket = !ticketId;

  const [ticketDetails, setTicketDetails] = useState({
    data: null as IPriorAuthTicketDetails | IClaimsTicketDetails | null,
    isLoading: false
  });

  // states for the infinite scroll
  const [activitiesSection, setActivitiesSection] = useState<IActivitySession>({
    pageNumber: 1,
    updateComponentKey: 1
  });

  const updateActivitySection = (
    updatedActivitiesSection: Partial<IActivitySession>
  ) => {
    setActivitiesSection(prev => ({...prev, ...updatedActivitiesSection}));
  };

  const [totalRecords, setTotalRecords] = useState(0);

  const [fieldOptions, setFieldOptions] = useState<{
    clinicUsersList: UserModelMapper[];
  }>({
    clinicUsersList: []
  });

  const {clinicUsersList} = fieldOptions;

  const {enqueueSnackbar} = useSnackbar();

  const [showConversationDataObj, setShowConversationDataObj] = useState<any>(
    {}
  );

  const classes = useStyles();
  const userPermissions = useSelector(
    (state: any) => state.auth.userPermissions
  );
  const methods = useForm<ITicketInputFields>({
    defaultValues: {
      patient: (null as any) as IPatient
    },
    mode: 'onChange'
  });

  const {control, errors, handleSubmit, setValue} = methods;

  const fetchTicketDetails = async (_ticketId: ITicketDetails['id']) => {
    try {
      setTicketDetails(prev => ({
        ...prev,
        isLoading: true
      }));
      const ticketDetailsRes = await getTicketDetails({
        clinicGroupId,
        clinicId,
        ticketId: _ticketId,
        params: {
          module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
        }
      });
      if (!isCreatingTicket) {
        setTicketDetails({
          data: ticketDetailsRes.data,
          isLoading: false
        });
        setValue(
          'assignedTo',
          ticketDetailsRes.data?.assigned_user?.id || null
        );
        setValue('notes', ticketDetailsRes.data?.description);
        setValue('title', ticketDetailsRes.data?.title);
        setValue('workflowStatusId', ticketDetailsRes.data?.workflow_status_id);
        setValue('priority', ticketDetailsRes.data?.priority);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
      setTicketDetails(prev => ({
        ...prev,
        isLoading: false
      }));
    }
  };

  const getSelectedStatusDetails = (
    selectedStatusId: IWorkFlowStatus['id']
  ) => {
    const selectedStatus = statusesList.find(s => s.id === selectedStatusId);
    if (!selectedStatus) return null;
    return selectedStatus;
  };

  const defaultTicketingSystemKeys: string[] = [
    'patient',
    'assignedTo',
    'notes',
    'title',
    'workflowStatusId',
    'priority'
  ];

  const handleErrorHandler: SubmitErrorHandler<ITicketInputFields> = formErrors => {
    const errorsObj: FieldError[] = Object.values(formErrors) as FieldError[];
    if (errorsObj?.[0]?.message) {
      enqueueSnackbar(t(errorsObj[0].message), SnackBarConfig('e'));
    }
  };

  const handleCreateOrUpdateTicketDetails: SubmitHandler<ITicketInputFields> = async values => {
    try {
      if (isCreatingTicket) {
        const payload: Parameters<typeof createTicket>['0']['payload'] = {
          patient_id: values.patient.id,
          title: values.title,
          workflow_id: workflowId
        };
        // iterate over values and create ticket
        if (values.assignedTo) {
          payload.assigned_to = values.assignedTo;
        }
        if (values.notes) {
          payload.description = values.notes;
        }
        payload.priority = values.priority ?? 0;
        // ADD MODULE SPECIFIC FIELDS
        const moduleInputFieldKeys = Object.keys(values).filter(
          key => !defaultTicketingSystemKeys.includes(key)
        );
        if (moduleInputFieldKeys.length) {
          payload.custom_fields = {};
          moduleInputFieldKeys.map(key => {
            if (
              values[key as keyof ITicketInputFields] &&
              payload.custom_fields
            ) {
              payload.custom_fields[key] =
                values[key as keyof ITicketInputFields];
            }
          });
        }

        const res = await createTicket({
          clinicGroupId,
          clinicId,
          payload,
          params: {
            module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
          }
        });
        // dispatch the event -> TICKET_CREATED
        if (clinicGroupId && clinicId) {
          CommunicationService.sendEvent({
            action: 'UPDATE_TICKET_STATUS_COUNT',
            data: null
          });
        }
        enqueueSnackbar(res.message, SnackBarConfig('s'));
        showTicketDetails(res.data.id);
        onSubmit();
      } else if (ticketDetails.data) {
        const selectedStatusDetails = getSelectedStatusDetails(
          values.workflowStatusId
        );
        let isStatusUpdate = false;
        const payload: Parameters<typeof updateTicket>[0]['payload'] = {
          title: values.title,
          workflow_id: ticketDetails.data.workflow_id
        };
        if (values.assignedTo) {
          payload.assigned_to = values.assignedTo;
        }
        if (values.notes) {
          payload.description = values.notes;
        }
        payload.priority = values.priority ?? 0;
        if (
          values.workflowStatusId &&
          values.workflowStatusId !== ticketDetails.data.workflow_status_id &&
          selectedStatusDetails
        ) {
          isStatusUpdate = true;
          payload.workflow_status_id = values.workflowStatusId;
          payload.workflow_status_code = selectedStatusDetails.code;
        }
        // ADD MODULE SPECIFIC FIELDS
        const moduleInputFieldKeys = Object.keys(values).filter(
          key => !defaultTicketingSystemKeys.includes(key)
        );
        if (moduleInputFieldKeys.length) {
          payload.custom_fields = {};
          moduleInputFieldKeys.map(key => {
            if (
              values[key as keyof ITicketInputFields] &&
              payload.custom_fields
            ) {
              payload.custom_fields[key] =
                values[key as keyof ITicketInputFields];
            }
          });
        }

        const res = await updateTicket({
          clinicGroupId,
          clinicId,
          ticketId: ticketDetails.data.id,
          payload,
          params: {
            module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
          }
        });
        // dispatch the event -> TICKET_CREATED
        if (isStatusUpdate && clinicGroupId && clinicId) {
          CommunicationService.sendEvent({
            action: 'UPDATE_TICKET_STATUS_COUNT',
            data: null
          });
        }
        enqueueSnackbar(res?.message, SnackBarConfig('s'));
        fetchTicketDetails(ticketDetails.data.id);
        onSubmit();
        setActivitiesSection(prev => ({
          ...prev,
          updateComponentKey: prev.updateComponentKey + 1
        }));
        // ** NOTE: Trigger an event after updated to get the latest details in the ticket timer panel */
        CommunicationService.sendEvent({
          action: 'TICKET_TIMER_PANEL_DATA_UPDATED',
          data: null
        });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };
  const updateTicketInfoForMarkAsDone = async ({
    patientTicketId
  }: {
    patientTicketId: ITicket['id'];
  }) => {
    try {
      const payload: Parameters<typeof updateTicket>[0]['payload'] = {};
      payload.is_completed = true;
      const res = await updateTicket({
        clinicGroupId,
        clinicId,
        ticketId: patientTicketId,
        payload,
        params: {
          module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
        }
      });
      CommunicationService.sendEvent({
        action: 'UPDATE_TICKET_STATUS_COUNT',
        data: null
      });
      enqueueSnackbar(res.message, SnackBarConfig('s'));
      fetchTicketDetails(patientTicketId);
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails(ticketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, clinicGroupId, clinicId]);

  const fetchAllPatientsWithMatchingText = async (
    searchText: string
  ): Promise<IPatient[] | undefined> => {
    try {
      if (searchText?.length < 3) return [];
      const params: Parameters<typeof getAllPatients>['0']['params'] = {};
      if (clinicId) {
        params.clinic_id = clinicId;
      }
      params.patient_name = searchText;
      const response = await getAllPatients({
        clinic_group_id: clinicGroupId,
        params
      });

      if (response.data.rows) {
        return response.data.rows;
      }
      return [] as IPatient[];
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const response = await getClinicUsersList(clinicGroupId, clinicId);
        if (response.data) {
          setFieldOptions(p => ({...p, clinicUsersList: response.data}));
        } else {
          setFieldOptions(p => ({...p, clinicUsersList: []}));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, clinicGroupId]);

  const statusesOfStepList = React.useMemo(() => {
    let allStatusOfSteps: IWorkFlowStatus[] = [];

    if (ticketDetails.data?.workflow_status_id) {
      const currentStatus = statusesList.find(
        i => i.id === ticketDetails.data?.workflow_status_id
      );
      if (currentStatus) {
        allStatusOfSteps = [currentStatus];
      }
      if (
        currentStatus?.nextSteps &&
        Array.isArray(currentStatus?.nextSteps) &&
        currentStatus.nextSteps.length > 0
      ) {
        const filteredNextSteps = getFilteredNextSteps(
          currentStatus,
          clinicTicketConfig,
          TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
        );
        allStatusOfSteps = [...allStatusOfSteps, ...filteredNextSteps];
      }
    }
    return allStatusOfSteps;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketDetails.data, statusesList, clinicTicketConfig]);

  const clinicUserOptionsList = clinicUsersList.map(clinicUser => {
    return {
      label: HelperUtils.commaSeparatedFullName(
        clinicUser.first_name,
        clinicUser.last_name
      ),
      value: clinicUser.id
    };
  });

  // DEFAULT FIELDS
  const ticketInputFields: ITicketInputFieldItems[] = [
    {
      formKey: 'patient',
      type: 'autocomplete',
      required: true,
      placeholder: t(
        'TICKETING_SYSTEM.TICKET_DETAILS.SELECT_PATIENT_PLACEHOLDER'
      ),
      label: `${t('TICKETING_SYSTEM.TICKET_DETAILS.SELECT_PATIENT_LABEL')} *`,
      classes: {
        listbox: classes.listbox
      },
      fetchOptions: fetchAllPatientsWithMatchingText,
      getOptionSelected: (option: IPatient, value: IPatient) => {
        if (value) {
          return option.id === value.id;
        } else {
          return false;
        }
      },
      getOptionLabel: (value: IPatient) => {
        return HelperUtils.commaSeparatedFullName(
          value.first_name,
          value.last_name,
          value.middle_name
        );
      },
      renderOption: (option: IPatient) => (
        <ListItemText
          className={classes.listItem}
          primary={HelperUtils.commaSeparatedFullName(
            option.first_name,
            option.last_name,
            option.middle_name
          )}
          secondary={option.emr_id}
        />
      ),
      controllerProps: {
        rules: {
          validate: value => {
            if (isCreatingTicket && !value) {
              return 'TICKETING_SYSTEM.TICKET_DETAILS.VALIDATION_ERROR.PATIENT_REQUIRED';
            }
            return true;
          }
        }
      },
      hideInEditTicket: true
    },
    {
      hidden: !isCreatingTicket,
      type: 'select',
      formKey: 'workflowStatusId',
      label: t('TICKETING_SYSTEM.TICKET_DETAILS.SELECT_STATUS_LABEL'),
      itemList: statusesOfStepList.map(status => ({
        label: status.label,
        value: status.id
      })),
      placeholder: t(
        'TICKETING_SYSTEM.TICKET_DETAILS.SELECT_STATUS_PLACEHOLDER'
      ),
      hideInCreateTicket: true
    },
    {
      label: t('TICKETING_SYSTEM.TICKET_DETAILS.ASSIGN_TO_LABEL'),
      formKey: 'assignedTo',
      type: 'select',
      placeholder: t('TICKETING_SYSTEM.TICKET_DETAILS.ASSIGN_TO_PLACEHOLDER'),
      itemList: clinicUserOptionsList,
      itemsHeaderLabel: t(
        'TICKETING_SYSTEM.TICKET_DETAILS.ASSIGN_TO_POPOVER_OPTIONS_TITLE'
      ),
      noItemLabel: t(
        'TICKETING_SYSTEM.TICKET_DETAILS.ASSIGN_TO_POPOVER_NO_USERS_LABEL'
      ),
      onClear: () => {
        setValue('assignedTo', null);
      }
    },
    {
      label: t('TICKETING_SYSTEM.TICKET_DETAILS.PRIORITY_LABEL'),
      formKey: 'priority',
      type: 'select',
      placeholder: t('TICKETING_SYSTEM.TICKET_DETAILS.PRIORITY_PLACEHOLDER'),
      itemList: PriorityOptionsList,
      itemsHeaderLabel: t(
        'TICKETING_SYSTEM.TICKET_DETAILS.PRIORITY_POPOVER_OPTIONS_TITLE'
      ),
      noItemLabel: t(
        'TICKETING_SYSTEM.TICKET_DETAILS.PRIORITY_POPOVER_NO_OPTIONS_TITLE'
      )
    }
  ];

  const dispatch = useDispatch();

  const refreshTicketDetails = () => {
    if (ticketDetails.data) {
      fetchTicketDetails(ticketDetails.data.id);
    }
  };

  const hasViewTicketActionHistoryPermission = useTicketingSystemPermission({
    module,
    groupPermission,
    objectFunction: TICKETING_SYSTEM_COMMON_PERMISSIONS.ACTION_HISTORY_LIST
  });

  const hasViewTicketAttachmentPermission = useTicketingSystemPermission({
    module,
    groupPermission,
    objectFunction: TICKETING_SYSTEM_COMMON_PERMISSIONS.READ_ATTACHMENT
  });

  const hasUpdateTicketPermission = useTicketingSystemPermission({
    module,
    groupPermission,
    objectFunction: TICKETING_SYSTEM_COMMON_PERMISSIONS.EDIT
  });

  // *** Messages Dialog component start *** //

  // function for opening messages conversation from material-UI dialog
  const onSendMessageClick = async (data: any): Promise<void> => {
    try {
      const lastMessageObj = await getLastMessage(
        clinicGroupId,
        clinicId,
        data.id
      );
      setShowConversationDataObj({
        show: true,
        messageId: lastMessageObj.data?.id,
        clinicId: clinicId,
        guarantorId: data.id
      });
    } catch (err: any) {
      enqueueSnackbar(err?.message ?? '', SnackBarConfig('e'));
      throw err;
    }
  };

  const handleConversationClose = () => {
    setShowConversationDataObj({
      show: false
    });
  };

  const renderMessagesDialog = () => {
    return (
      <MessagesConversationDialog
        groupId={clinicGroupId}
        clinicId={showConversationDataObj?.clinicId}
        messageId={showConversationDataObj?.messageId}
        guarantorId={showConversationDataObj?.guarantorId}
        handleClose={handleConversationClose}
        showConversation={showConversationDataObj.show}
        defaultEventType={messageEventType}
      />
    );
  };

  const refreshTicketGrid = () => {
    refreshTicketListGrid();
  };

  // *** Messages Dialog End *** //

  // AddAttachments and Action History --> Tabs: All, Comment, History will be rendered from the below function
  const renderAttachmentsAndActionHistoryComponent = (
    ticketDetailsData: ITicketDetails
  ) => {
    return (
      <div className="ticket-details-attachment-and-comment">
        {hasViewTicketAttachmentPermission && (
          <TicketingSystemAttachments
            clinicGroupId={clinicGroupId}
            clinicId={clinicId}
            patientTicketId={ticketDetailsData?.id}
            attachments={ticketDetailsData?.attachments}
            onAddAttachment={refreshTicketDetails}
            module={module}
            groupPermission={groupPermission}
          />
        )}
        {hasViewTicketActionHistoryPermission && (
          <TicketingSystemTicketActivity
            clinicGroupId={clinicGroupId}
            clinicId={clinicId}
            ticketId={ticketDetailsData?.id}
            statuses={statusesList}
            totalRecords={(value: any) => {
              return setTotalRecords(value);
            }}
            pageNumber={activitiesSection.pageNumber}
            componentKey={activitiesSection.updateComponentKey}
            updateActivitySection={updateActivitySection}
            refreshTicketGrid={refreshTicketGrid}
            module={module}
            groupPermission={groupPermission}
            renderTicketActivityItemContent={renderTicketActivityItemContent}
          />
        )}
      </div>
    );
  };
  const fetchNext = () => {
    const page = activitiesSection.pageNumber + 1;
    setActivitiesSection(prev => ({...prev, pageNumber: page}));
  };

  const renderInfiniteScroll = () => {
    return (
      <div className="parent-wrapper-infinite-scroll" id="scrollableDiv">
        <InfiniteScroll
          scrollableTarget="scrollableDiv"
          style={{overflow: 'hidden'}}
          dataLength={
            activitiesSection.pageNumber *
            PAGINATION_SIZE.TICKETING_ACTION_HISTORY
          }
          next={fetchNext}
          hasMore={
            activitiesSection.pageNumber *
              PAGINATION_SIZE.TICKETING_ACTION_HISTORY <
            totalRecords
          }
          loader={<Loader />}
          scrollThreshold={0.8}>
          {ticketDetails?.data &&
            renderAttachmentsAndActionHistoryComponent(ticketDetails?.data)}
        </InfiniteScroll>
      </div>
    );
  };
  return (
    <div className="ticketing-system-details-wrapper">
      <div className="title-wrapper">
        <Controller
          name="title"
          control={control}
          rules={{
            validate: value => {
              if (!value) {
                return 'TICKETING_SYSTEM.TICKET_DETAILS.VALIDATION_ERROR.TITLE_REQUIRED';
              }
              return true;
            }
          }}
          render={inputProps => (
            <FormField
              label={t('TICKETING_SYSTEM.TICKET_DETAILS.TITLE_LABEL')}
              placeholder={t(
                'TICKETING_SYSTEM.TICKET_DETAILS.TITLE_PLACEHOLDER'
              )}
              type="text"
              {...inputProps}
              error={errors['title']}
            />
          )}
        />
      </div>
      <div className="ticket-details-action">
        {hasUpdateTicketPermission && (
          <div className="ticket-details-update-and-timer-btn-wrapper">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(
                handleCreateOrUpdateTicketDetails,
                handleErrorHandler
              )}>
              {ticketDetails.data ? t('COMMON.UPDATE') : t('COMMON.CREATE')}
            </Button>
            <div>
              {ticketDetails.data && ticketId && (
                <TicketingSystemTimer
                  clinicGroupId={clinicGroupId}
                  clinicId={clinicId}
                  ticketId={ticketId}
                  selectedTicketDetails={ticketDetails}
                  module={module}
                  // updateActivitySection={updateActivitySection}
                  // componentKey={activitiesSection.updateComponentKey}
                />
              )}
            </div>
            {isWorkflowEndStatus(
              statusesList,
              ticketDetails.data?.workflow_status_id
            ) &&
              !ticketDetails.data?.is_completed && (
                <Link
                  onClick={(e: any) => {
                    updateTicketInfoForMarkAsDone({patientTicketId: ticketId});
                    e.stopPropagation();
                  }}>
                  {t('INCOMING_SMS.MARK_AS_DONE')}
                </Link>
              )}
          </div>
        )}
        <Tooltip title={t('COMMON.CLOSE') as string}>
          <IconButton
            onClick={onCloseTicketDetails}
            color="primary"
            className="close-button">
            <CloseSharp />
          </IconButton>
        </Tooltip>
      </div>
      <div className="ticket-details-main">
        <div className="notes-input-wrapper">
          <Controller
            name="notes"
            control={control}
            render={inputProps => (
              <FormField
                label={t('TICKETING_SYSTEM.TICKET_DETAILS.NOTES_LABEL')}
                placeholder={t(
                  'TICKETING_SYSTEM.TICKET_DETAILS.NOTES_PLACEHOLDER'
                )}
                type="textarea"
                {...inputProps}
                error={errors['notes']}
              />
            )}
          />
        </div>
        {/** Rendering the Attachments and Action History in Infinite Scrollable component */}
        {ticketDetails?.data && renderInfiniteScroll()}
      </div>
      <div className="ticket-details-aside">
        {isCreatingTicket && (
          <>
            {ticketInputFields
              .filter(i => !i?.hideInCreateTicket)
              .map(({controllerProps = {}, formKey, ...inputItem}, idx) => (
                <Controller
                  key={formKey}
                  control={control}
                  name={formKey}
                  {...controllerProps}
                  defaultValue={formKey === 'priority' ? 0 : null}
                  render={inputProps => {
                    let onChangeHandler = inputProps.onChange;
                    if (formKey === 'patient') {
                      onChangeHandler = (event: any, value: IPatient) => {
                        if (value) {
                          inputProps.onChange({
                            first_name: value.first_name,
                            last_name: value.last_name,
                            middle_name: value.middle_name,
                            id: value.id,
                            emr_id: value.emr_id
                          });
                        } else {
                          inputProps.onChange(null);
                        }
                      };
                    }
                    return (
                      <FormField
                        {...inputItem}
                        {...inputProps}
                        onChange={onChangeHandler}
                        error={errors[formKey]}
                      />
                    );
                  }}
                />
              ))}
            {/* RENDER OTHER MODULE SPECIFIC FIELDS */}
            {renderModuleInputFields && (
              <TicketingSystemTicketDetailsModuleFields
                isEditing={!isCreatingTicket}
                methods={methods}
                renderFields={renderModuleInputFields}
                ticketDetails={ticketDetails.data}
                refreshTicketDetails={refreshTicketDetails}
                isLoadingTicketDetails={false}
              />
            )}
          </>
        )}
        {!isCreatingTicket && ticketDetails.isLoading && !ticketDetails.data ? (
          <Loader />
        ) : (
          <>
            {ticketDetails.data && (
              <>
                {!hidePatientName && (
                  <div className="patient-send-message-field-wrapper">
                    <DropDownFieldValue
                      label={t('TICKETING_SYSTEM.TICKET_DETAILS.PATIENT')}
                      value={
                        <div className="patient-name-send-message-wrapper">
                          <div
                            className={
                              isPatientNameLink ? 'link-style patient-name' : ''
                            }
                            onClick={() => {
                              if (
                                isPatientNameLink &&
                                ticketDetails.data?.patient_id
                              ) {
                                dispatch(
                                  _patientDrawerDetails(
                                    clinicGroupId,
                                    clinicId,
                                    ticketDetails.data.patient_id,
                                    PatientsTabKeys[
                                      module as keyof typeof PatientsTabKeys
                                    ]
                                  )
                                );
                              }
                            }}>
                            {HelperUtils.commaSeparatedFullName(
                              ticketDetails.data.patient.first_name,
                              ticketDetails.data.patient.last_name,
                              ticketDetails.data.patient.middle_name
                            )}
                          </div>
                          {(Permission.hasIncomingSmsPermissions(
                            userPermissions,
                            AppPermissions.INCOMING_SMS.REPLY,
                            clinicGroupId
                          ) as any) &&
                            ticketDetails?.data?.patient?.guarantor
                              ?.notification_phone?.length > 0 && (
                              <div>
                                <Tooltip
                                  title={t(
                                    'TICKETING_SYSTEM.SEND_MESSAGE'
                                  ).toString()}>
                                  <div>
                                    <Link
                                      className="action"
                                      onClick={(e: any) => {
                                        onSendMessageClick(
                                          ticketDetails?.data?.patient
                                            ?.guarantor
                                        );
                                        e.stopPropagation();
                                      }}>
                                      {t('TICKETING_SYSTEM.SEND_MESSAGE')}
                                    </Link>
                                  </div>
                                </Tooltip>
                              </div>
                            )}
                        </div>
                      }
                    />
                  </div>
                )}
                {ticketInputFields
                  .filter(i => !i?.hideInEditTicket)
                  .map(({controllerProps = {}, formKey, ...inputItem}, idx) => (
                    <Controller
                      key={formKey}
                      control={control}
                      name={formKey}
                      {...controllerProps}
                      render={inputProps => {
                        let onChangeHandler = inputProps.onChange;
                        if (formKey === 'patient') {
                          onChangeHandler = (event: any, value: IPatient) => {
                            if (value) {
                              inputProps.onChange({
                                first_name: value.first_name,
                                last_name: value.last_name,
                                middle_name: value.middle_name,
                                id: value.id,
                                emr_id: value.emr_id
                              });
                            } else {
                              inputProps.onChange(null);
                            }
                          };
                        }
                        if (
                          formKey === 'workflowStatusId' &&
                          ticketDetails.data.is_completed
                        ) {
                          return (
                            <div className="completed-status">
                              <div className="field-label">
                                {inputItem.label}
                              </div>
                              <p>
                                {getWorkflowStatusLabel(
                                  statusesList,
                                  ticketDetails.data?.workflow_status_id
                                )}
                              </p>
                            </div>
                          );
                        }
                        if (
                          formKey === 'workflowStatusId' &&
                          (getWorkflowStatusLabel(
                            statusesList,
                            ticketDetails.data?.workflow_status_id
                          ) === 'Generate Code' ||
                            getWorkflowStatusLabel(
                              statusesList,
                              ticketDetails.data?.workflow_status_id
                            ) === 'Review Codes') &&
                          !(Permission.hasGenerateCodesOrReviewCodesPermissions(
                            userPermissions,
                            getWorkflowStatusLabel(
                              statusesList,
                              ticketDetails.data?.workflow_status_id
                            ) === 'Generate Code'
                              ? AppPermissions.PRIOR_AUTHORIZATION
                                  .GENERATE_CODES
                              : AppPermissions.PRIOR_AUTHORIZATION.REVIEW_CODES,
                            clinicGroupId
                          ) as any)
                        ) {
                          return (
                            <div className="completed-status">
                              <div className="field-label">
                                {inputItem.label}
                              </div>
                              <p>
                                {getWorkflowStatusLabel(
                                  statusesList,
                                  ticketDetails.data?.workflow_status_id
                                )}
                              </p>
                            </div>
                          );
                        }
                        return (
                          <FormField
                            {...inputItem}
                            {...inputProps}
                            onChange={onChangeHandler}
                            error={errors[formKey]}
                          />
                        );
                      }}
                    />
                  ))}
                {/* RENDER OTHER MODULE SPECIFIC FIELDS */}
                {renderModuleInputFields && (
                  <TicketingSystemTicketDetailsModuleFields
                    isEditing={!isCreatingTicket}
                    methods={methods}
                    renderFields={renderModuleInputFields}
                    ticketDetails={ticketDetails.data}
                    refreshTicketDetails={refreshTicketDetails}
                    isLoadingTicketDetails={ticketDetails.isLoading}
                  />
                )}
              </>
            )}
            {showConversationDataObj.show && renderMessagesDialog()}
          </>
        )}
      </div>
    </div>
  );
};

export default TicketingSystemTicketDetails;
